import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PublicLayout } from "@/components/layout/PublicLayout";
import DashboardLayout from "@/components/layout/DashboardLayout";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { lazy, Suspense } from "react";

// Lazy-loaded routes (not needed for initial paint)
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const ForCreators = lazy(() => import("./pages/ForCreators"));
const ForBrands = lazy(() => import("./pages/ForBrands"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const Contact = lazy(() => import("./pages/Contact"));
const Login = lazy(() => import("./pages/auth/Login"));
const Signup = lazy(() => import("./pages/auth/Signup"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const RoleSelection = lazy(() => import("./pages/auth/RoleSelection"));
const CreatorDashboard = lazy(() => import("./pages/creator/Dashboard"));
const CreatorProfile = lazy(() => import("./pages/creator/Profile"));
const CreatorCampaigns = lazy(() => import("./pages/creator/Campaigns"));
const CreatorCampaignDetail = lazy(() => import("./pages/creator/CampaignDetail"));
const CreatorApplications = lazy(() => import("./pages/creator/Applications"));
const CreatorSubmissions = lazy(() => import("./pages/creator/Submissions"));
const CreatorEarnings = lazy(() => import("./pages/creator/Earnings"));
const CreatorPayouts = lazy(() => import("./pages/creator/Payouts"));
const CreatorSettings = lazy(() => import("./pages/creator/Settings"));
const CreatorInstagram = lazy(() => import("./pages/creator/InstagramConnect"));
const BrandDashboard = lazy(() => import("./pages/brand/Dashboard"));
const BrandProfile = lazy(() => import("./pages/brand/Profile"));
const BrandCampaigns = lazy(() => import("./pages/brand/Campaigns"));
const NewCampaign = lazy(() => import("./pages/brand/NewCampaign"));
const BrandCampaignDetail = lazy(() => import("./pages/brand/CampaignDetail"));
const BrandApplications = lazy(() => import("./pages/brand/Applications"));
const BrandSubmissions = lazy(() => import("./pages/brand/Submissions"));
const BrandBudget = lazy(() => import("./pages/brand/Budget"));
const BrandSettings = lazy(() => import("./pages/brand/Settings"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminCreators = lazy(() => import("./pages/admin/Creators"));
const AdminBrands = lazy(() => import("./pages/admin/Brands"));
const AdminCampaigns = lazy(() => import("./pages/admin/Campaigns"));
const AdminApplications = lazy(() => import("./pages/admin/Applications"));
const AdminSubmissions = lazy(() => import("./pages/admin/Submissions"));
const AdminPayouts = lazy(() => import("./pages/admin/Payouts"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));
const AdminFlags = lazy(() => import("./pages/admin/Flags"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              {/* Public routes */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Index />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/for-creators" element={<ForCreators />} />
                <Route path="/for-brands" element={<ForBrands />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/contact" element={<Contact />} />
              </Route>

              {/* Auth routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/select-role" element={<RoleSelection />} />

              {/* Creator routes */}
              <Route path="/creator" element={
                <ProtectedRoute allowedRoles={["creator"]}>
                  <DashboardLayout role="creator" />
                </ProtectedRoute>
              }>
                <Route path="dashboard" element={<CreatorDashboard />} />
                <Route path="profile" element={<CreatorProfile />} />
                <Route path="campaigns" element={<CreatorCampaigns />} />
                <Route path="campaigns/:id" element={<CreatorCampaignDetail />} />
                <Route path="applications" element={<CreatorApplications />} />
                <Route path="submissions" element={<CreatorSubmissions />} />
                <Route path="earnings" element={<CreatorEarnings />} />
                <Route path="payouts" element={<CreatorPayouts />} />
                <Route path="settings" element={<CreatorSettings />} />
                <Route path="instagram" element={<CreatorInstagram />} />
                <Route path="instagram/connect" element={<CreatorInstagram />} />
              </Route>

              {/* Brand routes */}
              <Route path="/brand" element={
                <ProtectedRoute allowedRoles={["brand"]}>
                  <DashboardLayout role="brand" />
                </ProtectedRoute>
              }>
                <Route path="dashboard" element={<BrandDashboard />} />
                <Route path="profile" element={<BrandProfile />} />
                <Route path="campaigns" element={<BrandCampaigns />} />
                <Route path="campaigns/new" element={<NewCampaign />} />
                <Route path="campaigns/:id" element={<BrandCampaignDetail />} />
                <Route path="applications" element={<BrandApplications />} />
                <Route path="submissions" element={<BrandSubmissions />} />
                <Route path="budget" element={<BrandBudget />} />
                <Route path="settings" element={<BrandSettings />} />
              </Route>

              {/* Admin routes */}
              <Route path="/admin" element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <DashboardLayout role="admin" />
                </ProtectedRoute>
              }>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="creators" element={<AdminCreators />} />
                <Route path="brands" element={<AdminBrands />} />
                <Route path="campaigns" element={<AdminCampaigns />} />
                <Route path="applications" element={<AdminApplications />} />
                <Route path="submissions" element={<AdminSubmissions />} />
                <Route path="payouts" element={<AdminPayouts />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="flags" element={<AdminFlags />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;